import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  User, Store, FileText, MapPin, Phone, Mail, ChevronLeft, ShieldCheck, XCircle, 
  ExternalLink, Calendar, CheckCircle2, AlertTriangle, AlertCircle, TrendingUp, 
  Package, Star, ShoppingBag, Clock, DollarSign, CreditCard, Briefcase
} from 'lucide-react';

const SellerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const res = await axios.get(`/api/admin/sellers/${id}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDetails(res.data);
    } catch (error) {
      console.error('Error fetching details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!window.confirm('Approve this seller?')) return;
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.put(`/api/admin/sellers/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchDetails();
    } catch (error) {
      console.log(error);
    }
  };

  const getStatusConfig = (status) => {
    switch(status) {
      case 'approved': 
        return { label: 'Approved', icon: ShieldCheck, color: '#10b981', bg: '#d1fae5' };
      case 'rejected': 
        return { label: 'Rejected', icon: XCircle, color: '#ef4444', bg: '#fee2e2' };
      default: 
        return { label: 'Pending', icon: Clock, color: '#f59e0b', bg: '#fed7aa' };
    }
  };

  if (loading) {
    return (
      <div className="seller-details-loading">
        <div className="spinner"></div>
        <p>Loading seller details...</p>
      </div>
    );
  }

  if (!details) {
    return (
      <div className="seller-details-empty">
        <AlertTriangle size={64} />
        <h3>Seller Not Found</h3>
        <p>The requested seller details do not exist.</p>
        <button onClick={() => navigate('/admin/sellers')} className="btn-primary">Back to Sellers</button>
      </div>
    );
  }

  const seller = details.sellerDetails || {};
  const user = details.user || {};
  const status = user.sellerStatus || seller.sellerStatus || 'pending';
  const statusConfig = getStatusConfig(status);
  const StatusIcon = statusConfig.icon;
  const joinedDate = new Date(user.createdAt || seller.createdAt || Date.now()).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });

  const stats = {
    totalSales: seller.totalSales || 0,
    totalProducts: seller.productCount || 0,
    avgRating: seller.avgRating || 0,
    totalRevenue: seller.revenue || 0,
    totalOrders: seller.orderCount || 0
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'business', label: 'Business Info', icon: Store },
    { id: 'kyc', label: 'KYC Documents', icon: ShieldCheck },
    { id: 'bank', label: 'Bank Details', icon: CreditCard }
  ];

  return (
    <div className="seller-details-modern">
      {/* Header */}
      <div className="details-header">
        <button onClick={() => navigate('/admin/sellers')} className="back-btn">
          <ChevronLeft size={20} />
          Back to Sellers
        </button>
        <div className="header-actions">
          {status === 'pending' && (
            <button onClick={handleApprove} className="btn-approve">
              <CheckCircle2 size={16} />
              Approve Seller
            </button>
          )}
          <button className="btn-export">
            <FileText size={16} />
            Export Profile
          </button>
        </div>
      </div>

      {/* Profile Banner */}
      <div className="profile-banner">
        <div className="banner-bg"></div>
        <div className="profile-content">
          <div className="profile-avatar">
            <div className="avatar-wrapper">
              <span className="avatar-text">
                {seller.storeName?.charAt(0).toUpperCase() || user.username?.charAt(0).toUpperCase() || 'S'}
              </span>
            </div>
            <div className="status-badge" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
              <StatusIcon size={14} />
              {statusConfig.label}
            </div>
          </div>
          <div className="profile-info">
            <h1 className="profile-name">
              {seller.storeName || user.storeName || 'Store Name Not Provided'}
              {status === 'approved' && <CheckCircle2 size={20} className="verified-icon" />}
            </h1>
            <p className="profile-owner">
              <User size={14} />
              Owned by {user.username || seller.fullName}
            </p>
            <div className="profile-meta">
              <span className="meta-item">
                <Calendar size={12} />
                Joined {joinedDate}
              </span>
              <span className="meta-item">
                <Mail size={12} />
                {user.email}
              </span>
              <span className="meta-item">
                <Phone size={12} />
                {user.phone || 'Not provided'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon green">
            <TrendingUp size={20} />
          </div>
          <div className="stat-info">
            <h3>₹{stats.totalRevenue.toLocaleString()}</h3>
            <span>Total Revenue</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon blue">
            <Package size={20} />
          </div>
          <div className="stat-info">
            <h3>{stats.totalProducts}</h3>
            <span>Total Products</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange">
            <ShoppingBag size={20} />
          </div>
          <div className="stat-info">
            <h3>{stats.totalOrders}</h3>
            <span>Total Orders</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple">
            <Star size={20} />
          </div>
          <div className="stat-info">
            <h3>{typeof stats.avgRating === 'number' ? stats.avgRating.toFixed(1) : stats.avgRating}</h3>
            <span>Avg. Rating</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="details-tabs">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="info-card">
              <div className="card-header">
                <Store size={18} />
                <h4>Store Overview</h4>
              </div>
              <div className="card-body">
                <div className="description-box">
                  <p>{seller.storeDescription || 'No description provided by the seller.'}</p>
                </div>
                <div className="address-box">
                  <MapPin size={16} />
                  <span>{seller.storeAddress || user.storeAddress || 'Address not listed'}</span>
                </div>
              </div>
            </div>

            {status === 'rejected' && (
              <div className="rejection-card">
                <div className="rejection-icon">
                  <XCircle size={24} />
                </div>
                <div className="rejection-content">
                  <h5>Application Rejected</h5>
                  <p>{user.sellerReviewNotes || seller.rejectionReason || 'No reason provided by the administrator.'}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Business Info Tab */}
        {activeTab === 'business' && (
          <div className="business-tab">
            <div className="info-card">
              <div className="card-header">
                <Briefcase size={18} />
                <h4>Business Information</h4>
              </div>
              <div className="card-body">
                <div className="info-row">
                  <span className="info-label">Store Name</span>
                  <span className="info-value">{seller.storeName || user.storeName || '—'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Owner Name</span>
                  <span className="info-value">{user.username || seller.fullName || '—'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Email Address</span>
                  <span className="info-value">{user.email || '—'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Phone Number</span>
                  <span className="info-value">{user.phone || '—'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Joined Date</span>
                  <span className="info-value">{joinedDate}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* KYC Tab */}
        {activeTab === 'kyc' && (
          <div className="kyc-tab">
            <div className="info-card">
              <div className="card-header">
                <ShieldCheck size={18} />
                <h4>KYC Documents</h4>
              </div>
              <div className="card-body">
                <div className="kyc-grid">
                  <div className="kyc-item">
                    <div className="kyc-icon">
                      <FileText size={24} />
                    </div>
                    <div>
                      <div className="kyc-label">PAN Card</div>
                      <div className="kyc-value">{seller.panNumber || 'Not provided'}</div>
                    </div>
                  </div>
                  <div className="kyc-item">
                    <div className="kyc-icon">
                      <FileText size={24} />
                    </div>
                    <div>
                      <div className="kyc-label">Aadhaar Number</div>
                      <div className="kyc-value">{seller.aadhaarNumber || 'Not provided'}</div>
                    </div>
                  </div>
                </div>
                
                <div className="document-upload">
                  <FileText size={24} className="doc-icon" />
                  <div>
                    <h6>Identity Proof Document</h6>
                    <p>Original scan/photo provided during registration</p>
                  </div>
                  {seller.idProof ? (
                    <a href={`/${seller.idProof}`} target="_blank" rel="noreferrer" className="doc-link">
                      <ExternalLink size={16} />
                      View Document
                    </a>
                  ) : (
                    <span className="doc-missing">No file attached</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bank Tab */}
        {activeTab === 'bank' && seller.bankDetails && (
          <div className="bank-tab">
            <div className="info-card">
              <div className="card-header">
                <CreditCard size={18} />
                <h4>Bank Account Details</h4>
              </div>
              <div className="card-body">
                <div className="info-row">
                  <span className="info-label">Account Holder Name</span>
                  <span className="info-value">{seller.bankDetails.accountHolderName || '—'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Account Number</span>
                  <span className="info-value">{seller.bankDetails.accountNumber || '—'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">IFSC Code</span>
                  <span className="info-value">{seller.bankDetails.ifscCode || '—'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Bank Name</span>
                  <span className="info-value">{seller.bankDetails.bankName || '—'}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      
    </div>
  );
};

export default SellerDetails;